<template>
  <div class="content-page">
    <page-skeleton v-if="loading" variant="detail" class="mt-4" />

    <LessonContent
      v-else
      :lesson="module"
      :completed="isCompleted"
      :can-complete="canComplete"
      :completing="completing"
      :class-link="classLink"
      :initial-material-id="requestedMaterialId"
      :notes-hint="notesHint"
      :notes-label="false"
      @back="goBack"
      @toggle-complete="setCompletion"
    >
      <template #notes>
        <div class="notes">
          <!-- Retorno de 18/10 (card 17trt6jcyfb): as anotações vêm logo
               depois do material, sempre abertas. Exercícios e homework vêm
               abaixo, cada um num bloco que abre e fecha. -->
          <section class="nfold">
            <div class="nfold__toggle nfold__toggle--static">
              <span class="nfold__title">
                <i class="mdi mdi-note-text-outline" aria-hidden="true"></i>
                {{ $t("lesson.notes") }}
              </span>
              <span class="nfold__count">
                {{
                  $tc("lessonPage.notesCount", totalComments, {
                    count: totalComments,
                  })
                }}
              </span>
            </div>

            <div class="nfold__body">
              <p class="nfold__hint">{{ notesHint }}</p>
              <div class="nfold__grid">
                <div class="composer">
                  <h4>{{ $t("lessonPage.newSticker") }}</h4>
                  <p>{{ $t("lessonPage.stickerHint") }}</p>
                  <textarea
                    v-model="comment"
                    :placeholder="$t('lessonPage.stickerPlaceholder')"
                    rows="4"
                    @keydown.enter.exact.prevent="insertComment"
                  ></textarea>
                  <div class="composer__foot">
                    <span class="composer__count">
                      {{
                        $tc("lessonPage.notesCount", totalComments, {
                          count: totalComments,
                        })
                      }}
                    </span>
                    <button
                      class="composer__btn"
                      type="button"
                      :disabled="!comment.trim()"
                      @click="insertComment"
                    >
                      <i class="mdi mdi-note-plus-outline"></i>
                      {{ $t("lessonPage.pin") }}
                    </button>
                  </div>
                </div>

                <sticky-notes-board
                  :notes="comments"
                  :teacher-notes="teacherNotes"
                  :module-key="moduleId || $route.params.id"
                  @update-note="updateNote"
                />
              </div>
            </div>
          </section>

          <lesson-exercises
            v-if="$route.params.id"
            :course-class-id="$route.params.id"
          />

          <lesson-games
            v-if="$route.params.id"
            :course-class-id="$route.params.id"
          />

          <!-- Card 86akj6ghg: homework em área própria, fora do mural: tem
               anexo e ação de concluir, que um sticker arrastável não
               comporta. Retorno de 18/10: bloco que abre e fecha. -->
          <section
            v-if="homework.length"
            class="nfold"
            :class="{ 'is-open': homeworkOpen }"
          >
            <button
              type="button"
              class="nfold__toggle"
              :aria-expanded="String(homeworkOpen)"
              aria-controls="hwfold-body"
              @click="homeworkOpen = !homeworkOpen"
            >
              <span class="nfold__title">
                <i class="mdi mdi-notebook-edit-outline" aria-hidden="true"></i>
                {{ $t("lessonPage.homework") }}
              </span>
              <span class="nfold__count">
                {{
                  $tc("lessonPage.homeworkDone", homework.length, {
                    done: doneHomework,
                    total: homework.length,
                  })
                }}
              </span>
              <i
                class="mdi nfold__chev"
                :class="homeworkOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                aria-hidden="true"
              ></i>
            </button>

            <div
              v-show="homeworkOpen"
              id="hwfold-body"
              class="nfold__body homework"
            >
              <p class="homework__hint">
                {{ $t("lessonPage.homeworkHint") }}
              </p>

              <article
                v-for="item in homework"
                :key="item.id"
                class="hw"
                :class="{ 'hw--done': item.completed }"
              >
                <linkified-text tag="p" class="hw__body" :text="item.note" />

                <ul v-if="item.files && item.files.length" class="hw__files">
                  <li v-for="file in item.files" :key="file.id">
                    <a :href="file.url" target="_blank" rel="noopener">
                      <i class="mdi mdi-file-outline"></i>
                      {{ file.name }}
                    </a>
                  </li>
                </ul>

                <div class="hw__foot">
                  <span v-if="item.author" class="hw__author">
                    {{ item.author.name }}
                  </span>
                  <button
                    type="button"
                    class="hw__toggle"
                    :disabled="savingHomeworkId === item.id"
                    @click="toggleHomework(item)"
                  >
                    <i
                      class="mdi"
                      :class="
                        item.completed
                          ? 'mdi-checkbox-marked-circle'
                          : 'mdi-checkbox-blank-circle-outline'
                      "
                    ></i>
                    {{
                      item.completed
                        ? $t("lessonPage.completed")
                        : $t("lessonPage.markCompleted")
                    }}
                  </button>
                </div>

                <!-- Retorno de 14/10: o aluno pode devolver o homework ao
                   professor, por escrito ou com um arquivo pequeno. Enviar a
                   resposta já marca como concluído. -->
                <div
                  v-if="item.response && respondingId !== item.id"
                  class="hw__response"
                >
                  <div class="hw__response-head">
                    <span>
                      <i class="mdi mdi-reply-outline"></i>
                      {{
                        $t("lessonPage.yourResponse", {
                          date: formatResponseDate(item.response),
                        })
                      }}
                    </span>
                    <button
                      type="button"
                      class="hw__link"
                      @click="startResponse(item)"
                    >
                      {{ $t("lessonPage.edit") }}
                    </button>
                  </div>
                  <p v-if="item.response.text" class="hw__response-text">
                    {{ item.response.text }}
                  </p>
                  <a
                    v-if="item.response.file"
                    :href="item.response.file.url"
                    target="_blank"
                    rel="noopener"
                    class="hw__response-file"
                  >
                    <i class="mdi mdi-paperclip"></i>
                    {{ item.response.file.name }}
                  </a>
                </div>

                <div v-else-if="respondingId === item.id" class="hw__form">
                  <textarea
                    v-model="responseDraft.text"
                    class="hw__textarea"
                    rows="4"
                    maxlength="5000"
                    :placeholder="$t('lessonPage.responsePlaceholder')"
                  ></textarea>
                  <div class="hw__form-file">
                    <template
                      v-if="
                        item.response &&
                        item.response.file &&
                        !responseDraft.file &&
                        !responseDraft.removeFile
                      "
                    >
                      <span>
                        <i class="mdi mdi-paperclip"></i>
                        {{ item.response.file.name }}
                      </span>
                      <button
                        type="button"
                        class="hw__link"
                        @click="responseDraft.removeFile = true"
                      >
                        {{ $t("lessonPage.removeFile") }}
                      </button>
                    </template>
                    <template v-else>
                      <label class="hw__link">
                        <i class="mdi mdi-paperclip"></i>
                        {{
                          responseDraft.file
                            ? responseDraft.file.name
                            : $t("lessonPage.attachFile")
                        }}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,application/pdf"
                          hidden
                          @change="pickResponseFile"
                        />
                      </label>
                      <button
                        v-if="responseDraft.file"
                        type="button"
                        class="hw__link"
                        @click="responseDraft.file = null"
                      >
                        {{ $t("common.remove") }}
                      </button>
                    </template>
                  </div>
                  <div class="hw__form-actions">
                    <button
                      type="button"
                      class="hw__link"
                      :disabled="sendingResponse"
                      @click="cancelResponse"
                    >
                      {{ $t("common.cancel") }}
                    </button>
                    <button
                      type="button"
                      class="hw__send"
                      :disabled="sendingResponse || !canSendResponse(item)"
                      @click="sendResponse(item)"
                    >
                      {{
                        sendingResponse
                          ? $t("lessonPage.sending")
                          : $t("lessonPage.sendResponse")
                      }}
                    </button>
                  </div>
                </div>

                <button
                  v-else
                  type="button"
                  class="hw__link hw__respond"
                  @click="startResponse(item)"
                >
                  <i class="mdi mdi-reply-outline"></i>
                  {{ $t("lessonPage.respond") }}
                </button>
              </article>
            </div>
          </section>
        </div>
      </template>
    </LessonContent>
  </div>
</template>

<script>
import LessonContent from "@/components/connect/course/LessonContent.vue";
import LessonExercises from "@/components/exercises/LessonExercises.vue";
import LessonGames from "@/components/exercises/LessonGames.vue";
import StickyNotesBoard from "@/components/course/StickyNotesBoard.vue";
import LinkifiedText from "@/components/course/LinkifiedText.vue";
import pitchCustomerApi from "@/shared/pitchCustomerApi";
import { languageTag } from "@/plugins/i18n";

export default {
  name: "CourseContentPage",
  components: {
    LessonExercises,
    LessonGames,
    LessonContent,
    StickyNotesBoard,
    LinkifiedText,
  },
  async created() {
    await this.loadContent();
  },
  data() {
    return {
      module: {},
      moduleId: "",
      loading: true,
      comments: [],
      // Anotacoes recebidas do professor: somente leitura e fora da contagem
      // de "suas anotacoes".
      teacherNotes: [],
      // Card 86akj6ghg: homework tem área própria abaixo do mural.
      homework: [],
      savingHomeworkId: "",
      // Resposta ao homework em edição (uma por vez).
      respondingId: "",
      responseDraft: { text: "", file: null, removeFile: false },
      sendingResponse: false,
      totalComments: 0,
      comment: "",
      homeworkOpen: false,
      isCompleted: false,
      completedAt: null,
      // Vem da API: só o aluno sem professor conclui a própria aula.
      canComplete: false,
      completing: false,
    };
  },
  computed: {
    classLink() {
      return this.$route.query.classLink || "";
    },
    requestedMaterialId() {
      return this.$route.query.material || "";
    },
    notesHint() {
      return this.teacherNotes.length
        ? this.$t("lessonPage.notesHintShared")
        : this.$t("lesson.notesHint");
    },
    // Homework é opcional: o contador mostra o que já foi feito, não o que
    // "falta".
    doneHomework() {
      return this.homework.filter((item) => item.completed).length;
    },
  },
  methods: {
    // Card 86aknav25: o aluno corrige uma anotação já enviada ao mural.
    async updateNote({ id, note }) {
      try {
        await pitchCustomerApi.updateNote(id, note);

        const target = this.comments.find(
          (item) => String(item.id) === String(id)
        );
        if (target) target.note = note;

        this.$root.$emit("display-alert", {
          text: this.$t("lessonPage.noteUpdated"),
          type: 0,
        });
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message || this.$t("lessonPage.noteFailed"),
          type: 1,
        });
      }
    },
    // Card 86akj6ghg: marcar/desmarcar o homework. O estado local só muda
    // depois que a API confirma — o aluno não pode achar que concluiu algo
    // que não foi gravado.
    async toggleHomework(item) {
      this.savingHomeworkId = item.id;
      const done = !item.completed;

      try {
        const res = await pitchCustomerApi.setHomeworkCompletion(item.id, done);
        item.completed = res.data?.completed ?? done;
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("lessonPage.homeworkFailed"),
          type: 1,
        });
      } finally {
        this.savingHomeworkId = "";
      }
    },
    startResponse(item) {
      this.respondingId = item.id;
      this.responseDraft = {
        text: item.response?.text || "",
        file: null,
        removeFile: false,
      };
    },
    cancelResponse() {
      this.respondingId = "";
      this.responseDraft = { text: "", file: null, removeFile: false };
    },
    pickResponseFile(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        this.$root.$emit("display-alert", {
          text: this.$t("lessonPage.fileTooBig"),
          type: 1,
        });
        return;
      }
      this.responseDraft.file = file;
    },
    canSendResponse(item) {
      const keepsFile = !!item.response?.file && !this.responseDraft.removeFile;
      return (
        !!(this.responseDraft.text || "").trim() ||
        !!this.responseDraft.file ||
        keepsFile
      );
    },
    async sendResponse(item) {
      this.sendingResponse = true;
      try {
        const res = await pitchCustomerApi.respondHomework(item.id, {
          text: (this.responseDraft.text || "").trim(),
          file: this.responseDraft.file,
          removeFile: this.responseDraft.removeFile,
        });
        item.completed = true;
        this.$set(item, "response", res.data?.response || null);
        this.cancelResponse();
        this.$root.$emit("display-alert", {
          text: this.$t("lessonPage.responseSent"),
          type: 0,
        });
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("lessonPage.responseFailed"),
          type: 1,
        });
      } finally {
        this.sendingResponse = false;
      }
    },
    formatResponseDate(response) {
      const date = response?.respondedAt
        ? new Date(response.respondedAt)
        : null;
      if (!date || Number.isNaN(date.getTime())) return "";

      return new Intl.DateTimeFormat(languageTag(), {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "America/Sao_Paulo",
      }).format(date);
    },
    async loadContent() {
      this.loading = true;

      await pitchCustomerApi
        .getCourseContent(this.$route.params.id)
        .then((res) => {
          this.moduleId = this.$route.params.id;
          this.comments = res.data.notes.notes;
          this.totalComments = res.data.notes.count;
          this.teacherNotes = res.data.teacherNotes?.notes || [];
          this.homework = res.data.teacherNotes?.homework || [];
          this.module = res.data.content;
          this.isCompleted = !!res.data.completed;
          this.completedAt = res.data.completedAt;
          this.canComplete = !!res.data.canComplete;
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          // Aluno solo sem tutor: a API recusa o material com 422 (e não 403,
          // que desconectava o aluno). /curso explica a espera pelo tutor.
          if (err?.response?.status === 422) {
            this.$root.$emit("display-alert", {
              text:
                err.response.data?.message ||
                this.$t("moduleLessons.awaitingTutor"),
              type: 0,
              timeout: 2500,
            });
            this.$router.replace({ path: "/connect/aluno/curso" });
          }
        });
    },
    goBack() {
      this.$router.back();
    },
    setCompletion(completed) {
      this.completing = true;

      pitchCustomerApi
        .setCourseCompletion(this.moduleId, completed)
        .then((res) => {
          this.isCompleted = !!res.data.completed;
          this.completedAt = res.data.completedAt;
          this.$root.$emit("display-alert", {
            text: this.isCompleted
              ? this.$t("lessonPage.lessonCompleted")
              : this.$t("lessonPage.lessonReopened"),
            type: 0,
            timeout: 1500,
          });
        })
        .catch((err) => {
          this.$root.$emit("display-alert", {
            text:
              err?.response?.data?.message ||
              this.$t("lessonPage.completionFailed"),
            type: 1,
          });
        })
        .finally(() => {
          this.completing = false;
        });
    },
    insertComment() {
      if (this.comment.trim() === "") return;

      pitchCustomerApi
        .addCourseContentNote(this.moduleId, this.comment)
        .then((res) => {
          this.comments = [...this.comments, res.data.note];
          this.totalComments = this.comments.length;
          this.$root.$emit("display-alert", {
            text: this.$t("lessonPage.notePinned"),
            type: 0,
            timeout: 1500,
          });
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("lessonPage.noteSaveFailed"),
            type: 1,
            timeout: 1500,
          });
        });

      this.comment = "";
    },
  },
  watch: {
    "$route.fullPath"() {
      this.loadContent();
    },
  },
};
</script>

<style scoped>
.content-page {
  padding: clamp(16px, 3vw, 32px) clamp(16px, 4vw, 40px) 0;
}
.content-page__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  font-family: "Poppins", sans-serif;
  color: var(--dk-ink-soft, #6e6a79);
}

/* anotacoes: exercicios, anotacoes e homework empilhados, cada um na
   largura toda (retorno de 16/10). */
.notes {
  display: grid;
  gap: 16px;
}
.nfold {
  border: 1px solid var(--dk-line, #ece6f5);
  border-radius: 16px;
  background: var(--dk-surface, #fff);
  font-family: "Poppins", sans-serif;
}
.nfold__toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 20px;
  border: 0;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.nfold__toggle--static {
  cursor: default;
}
.nfold__toggle:focus-visible {
  outline: 3px solid rgba(232, 39, 111, 0.3);
  outline-offset: -3px;
  border-radius: 16px;
}
.nfold__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: var(--dk-ink, #2b2540);
}
.nfold__title .mdi {
  color: var(--dk-pink, #e8276f);
}
.nfold__count {
  margin-left: auto;
  font-size: 13px;
  color: var(--dk-muted, #7a7590);
}
.nfold__chev {
  font-size: 22px;
  color: var(--dk-muted, #7a7590);
}
.nfold__body {
  padding: 0 20px 20px;
}
.nfold__hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--dk-muted, #7a7590);
}
.nfold__grid {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 2fr;
  gap: 16px;
  align-items: start;
}
.composer {
  border: 1px solid var(--dk-line, #eae7f0);
  border-radius: 18px;
  background: var(--dk-surface, #fff);
  padding: 20px;
  font-family: "Poppins", sans-serif;
}

/* Card 86akj6ghg: corpo do bloco de homework. */
.homework {
  display: grid;
  gap: 12px;
}
.homework__hint {
  margin: 0;
  font-size: 0.82rem;
  color: var(--dk-muted, #6b7280);
}
.hw {
  border: 1px solid var(--dk-line, #e3e0ec);
  border-left: 4px solid #4f46e5;
  border-radius: 14px;
  background: var(--dk-surface, #fff);
  padding: 16px 18px;
}
/* Concluído continua legível: recua o destaque, não some com o conteúdo. */
.hw--done {
  border-left-color: var(--dk-teal, #16a34a);
  background: var(--dk-paper, #fafafa);
}
.hw__body {
  white-space: pre-wrap;
  margin: 0 0 10px;
  color: var(--dk-ink, #1f2937);
  line-height: 1.5;
}
.hw__files {
  list-style: none;
  margin: 0 0 10px;
  padding: 0;
  display: grid;
  gap: 6px;
}
.hw__files a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #4338ca;
  font-size: 0.85rem;
  text-decoration: none;
}
.hw__files a:hover {
  text-decoration: underline;
}
.hw__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.hw__author {
  font-size: 0.8rem;
  color: var(--dk-muted, #6b7280);
}
.hw__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(79, 70, 229, 0.35);
  border-radius: 999px;
  padding: 6px 14px;
  background: transparent;
  color: #4338ca;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}
.hw__toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.hw--done .hw__toggle {
  border-color: rgba(22, 163, 74, 0.4);
  color: var(--dk-teal-deep, #15803d);
}
/* Retorno de 14/10: resposta do aluno ao homework. */
.hw__link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  padding: 0;
  color: #4338ca;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}
.hw__link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.hw__respond {
  margin-top: 12px;
}
.hw__response {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--dk-paper, #f5f3ff);
}
.hw__response-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.8rem;
  color: var(--dk-muted, #6b7280);
}
.hw__response-text {
  margin: 6px 0 0;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--dk-ink, #1f2937);
}
.hw__response-file {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  color: #4338ca;
  font-size: 0.85rem;
  word-break: break-all;
}
.hw__form {
  margin-top: 12px;
  display: grid;
  gap: 8px;
}
.hw__textarea {
  width: 100%;
  border: 1px solid var(--dk-line, #d6d3e0);
  border-radius: 10px;
  padding: 10px 12px;
  font: inherit;
  font-size: 0.9rem;
  resize: vertical;
}
.hw__form-file {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 0.85rem;
  color: var(--dk-ink-soft, #374151);
}
.hw__form-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
}
.hw__send {
  border: 0;
  border-radius: 999px;
  padding: 7px 16px;
  background: #4f46e5;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}
.hw__send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.composer h4 {
  margin: 0;
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 1.2rem;
  color: var(--dk-ink, #211b2e);
}
.composer p {
  margin: 8px 0 14px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--dk-ink-soft, #6e6a79);
}
.composer textarea {
  width: 100%;
  border: 1px solid var(--dk-line-strong, #ded9e8);
  border-radius: 12px;
  padding: 12px 14px;
  font-family: "Poppins", sans-serif;
  font-size: 13.5px;
  color: var(--dk-ink, #211b2e);
  resize: vertical;
  min-height: 96px;
  outline: none;
  transition: border-color 0.18s ease;
}
.composer textarea:focus {
  border-color: var(--accent, var(--dk-pink, #e8276f));
}
.composer__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
}
.composer__count {
  font-size: 12px;
  color: var(--dk-faint, #9a96a6);
}
.composer__btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 0;
  border-radius: 999px;
  padding: 10px 18px;
  font-family: "Poppins", sans-serif;
  font-size: 13.5px;
  font-weight: 600;
  color: #fff;
  background: var(--dk-ink, #211b2e);
  cursor: pointer;
  transition: background 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
}
.composer__btn:hover:not(:disabled) {
  background: var(--accent, var(--dk-pink, #e8276f));
}
.composer__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.composer__btn .mdi {
  font-size: 17px;
}

@media (max-width: 760px) {
  .nfold__grid {
    grid-template-columns: 1fr;
  }
}
</style>
